import { action, computed, makeAutoObservable } from "mobx";
import { useLocalObservable } from "mobx-react-lite";
import { createContext, ReactNode, useContext } from "react";

import { SignalHookStore } from "./signal-hook-store";
import { SignalFormState, SignalHookFormState, SignalHookScopeType } from "./types";
import { createEmptyHook, signalToFormState } from "./utils";

import { Signal } from "@/queries/signals";

export class SignalFormStore {
  name = "";
  prompt = "";
  enabled = true;
  hooks: SignalHookStore[] = [new SignalHookStore()];

  constructor(initialSignal?: Signal) {
    if (initialSignal) {
      const state = signalToFormState(initialSignal);
      this.name = state.name;
      this.prompt = state.prompt;
      this.enabled = state.enabled;
      this.hooks = state.hooks.map(hook => new SignalHookStore(hook));
    }

    makeAutoObservable(this);
  }

  @computed
  get formState(): SignalFormState {
    return {
      name: this.name,
      prompt: this.prompt,
      enabled: this.enabled,
      hooks: this.hooks.map(hook => hook.formState),
    };
  }

  @action
  setName(name: string) {
    this.name = name;
  }

  @action
  setPrompt(prompt: string) {
    this.prompt = prompt;
  }

  @action
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  @action
  updateHook(hookId: string, updater: (hook: SignalHookFormState) => SignalHookFormState) {
    const hook = this.hooks.find(item => item.id === hookId);
    hook?.update(updater);
  }

  @action
  addHook() {
    if (this.hooks.some(hook => hook.scopeType === SignalHookScopeType.ALL_REQUESTS)) {
      return;
    }

    this.hooks = [
      ...this.hooks,
      new SignalHookStore(createEmptyHook(SignalHookScopeType.PROMPT_LABEL)),
    ];
  }

  @action
  removeHook(hookId: string) {
    if (this.hooks.length === 1) {
      return;
    }

    this.hooks = this.hooks.filter(hook => hook.id !== hookId);
  }
}

const SignalFormContext = createContext<SignalFormStore | null>(null);

export const SignalFormProvider = ({
  children,
  initialSignal,
}: {
  children: ReactNode;
  initialSignal?: Signal;
}) => {
  const store = useLocalObservable(() => new SignalFormStore(initialSignal));

  return <SignalFormContext.Provider value={store}>{children}</SignalFormContext.Provider>;
};

export const useSignalFormStore = () => {
  const store = useContext(SignalFormContext);

  if (!store) {
    throw new Error("useSignalFormStore must be used within a SignalFormProvider");
  }

  return store;
};
